import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import PageShell from "components/bento/PageShell";
import TabsNavigator from "components/TabsNavigator";
import CreateTab from "./CreateTab";
import HistoryTab from "./HistoryTab";
import DraftsTab from "./DraftsTab";

const SalesDocuments = () => {
  const { t } = useTranslation();
  // Bumped whenever the Create tab generates a new document, so the History
  // tab's list picks it up without a manual reload — tabs stay mounted under
  // TabsNavigator, so this can't rely on remount (same pattern as departureBatch).
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  // What the Create tab is prefilled from: { doc, seq, mode } — mode
  // "duplicate" (History → Document Detail's "Duplicate" action) or "draft"
  // (Drafts tab, or the Create tab's own Save Draft). `seq` changes on every
  // click (even re-opening the same document) so CreateTab's effect always
  // re-applies it. See CreateTab's `seed` prop.
  const [formSeed, setFormSeed] = useState(null);

  const openInCreateTab = (doc, mode) => {
    setFormSeed((prev) => ({ doc, mode, seq: (prev?.seq || 0) + 1 }));
    setActiveTab(0);
  };

  // Keeps the seed pointing at the draft's latest saved state WITHOUT bumping
  // `seq` — so the mounted form isn't re-prefilled, but coming back to the
  // Create tab (which remounts it) restores the saved draft, not a stale copy.
  const handleDraftSaved = (doc) => {
    setFormSeed((prev) => ({ doc, mode: "draft", seq: prev?.seq || 0 }));
  };

  const tabItems = [
    {
      text: t("salesDocuments.tabCreate"),
      component: (
        <CreateTab
          onCreated={() => setRefreshSignal((n) => n + 1)}
          seed={formSeed}
          onDraftSaved={handleDraftSaved}
          onReset={() => setFormSeed(null)}
        />
      ),
      slug: "sales-documents/create",
    },
    // Drafts are part of creating — gated by the same page slug as Create, so
    // no new permission needs granting to existing roles. Refetches on every
    // visit (hidden tabs are unmounted), so it needs no refresh signal.
    { text: t("salesDocuments.tabDrafts"), component: <DraftsTab onOpenDraft={(doc) => openInCreateTab(doc, "draft")} />, slug: "sales-documents/create" },
    { text: t("salesDocuments.tabHistory"), component: <HistoryTab refreshSignal={refreshSignal} onDuplicate={(doc) => openInCreateTab(doc, "duplicate")} />, slug: "sales-documents/history" },
  ];

  return (
    <PageShell title={t("salesDocuments.title")} subtitle={t("salesDocuments.subtitle")}>
      <TabsNavigator tabItems={tabItems} pageSlug="sales-documents" activeTab={activeTab} onTabChange={setActiveTab} />
    </PageShell>
  );
};

export default SalesDocuments;
